import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCompare } from "../lib/compare";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Clock, X, ArrowLeft, Trophy, Calculator, MessageCircle } from "lucide-react";
import { parcelWhatsappUrl } from "../lib/whatsapp";

const fmtINR = (v) => v >= 10000000 ? `₹${(v / 10000000).toFixed(2)} Cr` : v >= 100000 ? `₹${(v / 100000).toFixed(1)} L` : `₹${v}`;

export default function Compare() {
  const navigate = useNavigate();
  const { ids, remove, clear } = useCompare();
  const [parcels, setParcels] = useState([]);
  const [calcs, setCalcs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (ids.length === 0) { setLoading(false); return; }
      try {
        const results = await Promise.all(ids.map((id) => api.get(`/parcels/${id}`).then((r) => r.data).catch(() => null)));
        const clean = results.filter(Boolean);
        setParcels(clean);
        const calcMap = {};
        await Promise.all(clean.map(async (p) => {
          try {
            const { data } = await api.post("/calc/far", {
              size_acres: p.size_acres, road_width_ft: p.road_width_ft,
              zone: "Residential", project_type: "Residential",
            });
            calcMap[p.id] = data;
          } catch {}
        }));
        setCalcs(calcMap);
      } catch { toast.error("Failed to load comparison"); }
      finally { setLoading(false); }
    })();
  }, [ids]);

  const bestBy = (key, higher = true) => {
    if (parcels.length === 0) return null;
    return parcels.reduce((best, p) => {
      const v = key(p);
      const b = key(best);
      if (higher) return v > b ? p : best;
      return v < b ? p : best;
    }, parcels[0]).id;
  };

  const highest = useMemo(() => ({
    score: bestBy((p) => p.legal_score),
    price: bestBy((p) => p.price_per_acre, false),
    far: bestBy((p) => calcs[p.id]?.far || 0),
    saleable: bestBy((p) => calcs[p.id]?.saleable_sqft || 0),
  }), [parcels, calcs]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-slate-400">Loading comparison…</div>;

  if (parcels.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <Trophy className="mx-auto h-10 w-10 text-[#10B981]" />
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight">No parcels shortlisted yet</h1>
        <p className="mt-2 text-slate-400 text-sm">Head to the map and click <span className="text-[#10B981]">+ Compare</span> on any parcel card to add it here (up to 3).</p>
        <Button className="mt-8 rounded-full bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => navigate("/map")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Map
        </Button>
      </div>
    );
  }

  const rows = [
    { label: "Corridor",       get: (p) => p.corridor_location, testid: "row-corridor" },
    { label: "Size",           get: (p) => `${p.size_acres} Acres`, testid: "row-size" },
    { label: "Road Frontage",  get: (p) => `${p.road_width_ft} ft`, testid: "row-road" },
    { label: "Owner",          get: (p) => p.owner_category, testid: "row-owner" },
    { label: "Khata",          get: (p) => p.khata_type, testid: "row-khata" },
    { label: "Conversion",     get: (p) => p.conversion_status, testid: "row-conv" },
    { label: "Price / Acre",   get: (p) => fmtINR(p.price_per_acre), highlight: "price", testid: "row-price" },
    { label: "Total Price",    get: (p) => fmtINR(p.price_per_acre * p.size_acres), testid: "row-total-price" },
    { label: "Legal Score",    get: (p) => `${p.legal_score}/10`, highlight: "score", testid: "row-score" },
    { label: "Verified",       get: (p) => p.verified ? "Yes" : "Pending Audit", testid: "row-verified" },
    { label: "Est. FAR",       get: (p) => calcs[p.id]?.far ?? "—", highlight: "far", testid: "row-far" },
    { label: "Built-up (sqft)",get: (p) => calcs[p.id]?.buildup_sqft?.toLocaleString() ?? "—", testid: "row-buildup" },
    { label: "Saleable (sqft)",get: (p) => calcs[p.id]?.saleable_sqft?.toLocaleString() ?? "—", highlight: "saleable", testid: "row-saleable" },
    { label: "Max Height",     get: (p) => `${calcs[p.id]?.max_height_m ?? "—"} m`, testid: "row-height" },
    { label: "Rec. Split",     get: (p) => calcs[p.id]?.recommended_split ?? "—", testid: "row-split" },
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Deal Comparison</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Side-by-side shortlist</h1>
          <p className="mt-2 text-slate-400 text-sm">Winners are highlighted with a Pulse Green ring for each metric.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="compare-clear" variant="outline" className="border-[#334155] bg-transparent text-white hover:bg-[#1E293B] hover:text-white rounded-full" onClick={() => { clear(); navigate("/map"); }}>
            Clear all
          </Button>
          <Button className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => navigate("/map")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Map
          </Button>
        </div>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {parcels.map((p) => (
          <Card key={p.id} data-testid="compare-parcel-card" className="bg-[#1E293B] border-[#334155] p-5 relative">
            <button data-testid="compare-remove-btn" aria-label="Remove" onClick={() => remove(p.id)} className="absolute top-3 right-3 rounded-full p-1 hover:bg-[#0F172A] transition-colors">
              <X className="h-4 w-4 text-slate-400" />
            </button>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{p.corridor_location}</div>
            <h3 className="mt-2 font-display text-xl font-semibold tracking-tight leading-tight">{p.title}</h3>
            <div className="mt-3 flex items-center gap-2">
              {p.verified ? (
                <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-500/40 text-amber-400"><Clock className="h-3 w-3 mr-1" />Audit</Badge>
              )}
              <Badge className="bg-[#0F172A] border-[#334155] text-slate-300">{p.khata_type}</Badge>
            </div>
            <div className="mt-4 text-[#10B981] font-display text-2xl font-bold">{fmtINR(p.price_per_acre)}<span className="text-xs text-slate-500 ml-1">/acre</span></div>
            <a
              data-testid="compare-whatsapp-btn"
              href={parcelWhatsappUrl(p, { mode: "compare" })}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white h-9 text-xs font-medium transition-colors"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Enquire on WhatsApp
            </a>
          </Card>
        ))}
        {Array.from({ length: Math.max(0, 3 - parcels.length) }).map((_, i) => (
          <div key={`slot-${i}`} className="border border-dashed border-[#334155] rounded-xl p-5 grid place-items-center text-slate-500 text-sm min-h-[180px]">
            + Add another parcel from the map
          </div>
        ))}
      </div>

      <Card className="mt-10 bg-[#1E293B] border-[#334155] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#334155]">
            <tr>
              <th className="text-left p-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Metric</th>
              {parcels.map((p) => (
                <th key={p.id} className="text-left p-4 text-white font-display font-semibold tracking-tight text-sm">{p.corridor_location} · {p.size_acres} Ac</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} data-testid={r.testid} className="border-b border-[#334155]/50 hover:bg-[#0F172A]/50 transition-colors">
                <td className="p-4 text-slate-400 text-xs uppercase tracking-[0.15em]">{r.label}</td>
                {parcels.map((p) => {
                  const isWin = r.highlight && highest[r.highlight] === p.id && parcels.length > 1;
                  return (
                    <td key={p.id} className={`p-4 ${isWin ? "text-[#10B981] font-semibold" : "text-white"}`}>
                      <div className={`inline-flex items-center gap-2 ${isWin ? "px-2 py-1 rounded-md bg-[#10B981]/10 ring-1 ring-[#10B981]/40" : ""}`}>
                        {r.get(p)}
                        {isWin && <Trophy className="h-3.5 w-3.5" />}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="mt-8 flex items-center gap-3">
        <Button className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => navigate("/calculator")}>
          <Calculator className="mr-2 h-4 w-4" /> Model this deal further
        </Button>
        <Button variant="outline" className="rounded-full border-[#334155] bg-transparent text-white hover:bg-[#1E293B] hover:text-white" onClick={() => navigate("/bookings")}>
          Book advisory
        </Button>
      </div>
    </div>
  );
}
